import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import { SelectField, TextField, ToggleSwitch } from "@/components/base/FormControls";
import PermissionPicker from "@/pages/admin/users/components/PermissionPicker";
import { ALL_PERMISSION_CODES, DEFAULT_CASHIER_PERMISSIONS } from "@/pages/admin/users/permissions";
import { callUsersApi } from "@/lib/usersApi";
import { useToast } from "@/context/ToastContext";
import type { Branch, StaffMember } from "@/types";

interface StaffFormModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  branches: Branch[];
  editing: StaffMember | null;
}

export default function StaffFormModal({
  open,
  onClose,
  onSaved,
  branches,
  editing,
}: StaffFormModalProps) {
  const toast = useToast();
  const isEdit = Boolean(editing);

  const [fullName, setFullName] = useState("");
  const [lastName, setLastName] = useState("");
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [branchId, setBranchId] = useState("");
  const [active, setActive] = useState(true);
  const [permissions, setPermissions] = useState<string[]>(DEFAULT_CASHIER_PERMISSIONS);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setPin("");
    setPinConfirm("");
    if (editing) {
      setFullName(editing.full_name);
      setLastName(editing.last_name);
      setBranchId(editing.branch_id ?? "");
      setActive(editing.active);
      setPermissions(editing.permission_codes);
    } else {
      setFullName("");
      setLastName("");
      setBranchId(branches[0]?.id ?? "");
      setActive(true);
      setPermissions(DEFAULT_CASHIER_PERMISSIONS);
    }
  }, [open, editing, branches]);

  const togglePermission = (code: string) => {
    setPermissions((prev) =>
      prev.includes(code) ? prev.filter((item) => item !== code) : [...prev, code],
    );
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!fullName.trim() || !lastName.trim()) {
      setError("El nombre y el apellido son obligatorios.");
      return;
    }
    if (!isEdit) {
      if (!/^[0-9]{6}$/.test(pin)) {
        setError("El PIN debe tener exactamente 6 dígitos.");
        return;
      }
      if (pin !== pinConfirm) {
        setError("El PIN y su confirmación no coinciden.");
        return;
      }
    }

    setSubmitting(true);
    const body = isEdit
      ? {
          action: "update_cashier",
          id: editing?.id,
          full_name: fullName.trim(),
          last_name: lastName.trim(),
          branch_id: branchId || null,
          active,
          permissions,
        }
      : {
          action: "create_cashier",
          full_name: fullName.trim(),
          last_name: lastName.trim(),
          pin,
          branch_id: branchId || null,
          permissions,
        };

    const result = await callUsersApi(body);
    setSubmitting(false);

    if (!result.ok) {
      setError(result.error ?? "No se pudo guardar.");
      return;
    }

    toast.success(isEdit ? "Cajero actualizado correctamente." : "Cajero creado correctamente.");
    onSaved();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={isEdit ? "Editar cajero" : "Nuevo cajero"}
      subtitle={
        isEdit
          ? "Actualiza los datos, la sucursal y los permisos del cajero."
          : "El cajero iniciará sesión con el PIN de 6 dígitos que definas aquí."
      }
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="staff-form"
            disabled={submitting}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
          >
            {submitting ? <i className="ri-loader-4-line animate-spin text-base"></i> : null}
            {isEdit ? "Guardar cambios" : "Crear cajero"}
          </button>
        </>
      }
    >
      <form id="staff-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="Nombre"
            name="full_name"
            value={fullName}
            onChange={setFullName}
            placeholder="Juan"
            required
          />
          <TextField
            label="Apellido"
            name="last_name"
            value={lastName}
            onChange={setLastName}
            placeholder="Pérez"
            required
          />
        </div>

        {!isEdit ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label="PIN (6 dígitos)"
              name="pin"
              value={pin}
              onChange={(value) => setPin(value.replace(/[^0-9]/g, "").slice(0, 6))}
              placeholder="000000"
              inputMode="numeric"
              maxLength={6}
              required
              hint="Solo números, exactamente 6 dígitos."
            />
            <TextField
              label="Confirmar PIN"
              name="pin_confirm"
              value={pinConfirm}
              onChange={(value) => setPinConfirm(value.replace(/[^0-9]/g, "").slice(0, 6))}
              placeholder="000000"
              inputMode="numeric"
              maxLength={6}
              required
            />
          </div>
        ) : null}

        <SelectField
          label="Sucursal"
          name="branch_id"
          value={branchId}
          onChange={setBranchId}
          placeholder="Sin sucursal"
          options={branches.map((branch) => ({
            value: branch.id,
            label: branch.active ? branch.name : `${branch.name} (inactiva)`,
          }))}
          hint="El cajero operará el POS en esta sucursal."
        />

        {isEdit ? (
          <ToggleSwitch
            label="Usuario activo"
            description="Si lo desactivas, no podrá iniciar sesión con su PIN."
            checked={active}
            onChange={setActive}
          />
        ) : null}

        <PermissionPicker
          selected={permissions}
          onToggle={togglePermission}
          onSelectAll={() => setPermissions(ALL_PERMISSION_CODES)}
          onClear={() => setPermissions([])}
        />

        {error ? (
          <div className="animate-shake flex items-start gap-2.5 rounded-md border border-primary-300 bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
            <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
              <i className="ri-error-warning-line text-base"></i>
            </span>
            <span className="flex-1 leading-snug">{error}</span>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}